import { useAppDispatch, useAppSelector } from "../../redux/store";
import { InputContainer, LoginContainer } from "./styled";
import { BarekamIcon, IconVariants } from "../components/icon/barekam-icon";
import { OverridableStringUnion } from '@mui/types';
import BarekamButton, {
  AuthButtons,
  SimpleButtons,
} from "../components/button";
import "react-phone-input-2/lib/material.css";
import BarekamPhoneInput from "./phone-input/phone-input";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  changeErrors,
  registerAction,
  registerWithGoogleAccountAction,
} from "../../redux/slices/register-slice";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import BarekamText from "../components/text";
import BarekamInput from "../components/input";
import "react-toastify/dist/ReactToastify.css";
import { User } from "../shared/models/user/user";
import { Message, MessageSections } from "../components/message/message";
import { Alert, Box, Divider, Grid } from "@mui/material";
import { UserTypeEnum } from "../shared/enum/user-type.enum";
import { Footer } from "../footer/footer";
import { UserGroups } from "../../router/router";
import { ARMENIA_COUNTRY_ID } from "../../constants";
import { getMessage } from "../shared/helper-functions";
import { loginWithGoogleAccountAction } from "../../redux/slices/login-slice";
import TypeSelectionBlock from "./TypeSelectionBlock";
import BarekamCheckbox from "../components/checkbox";

const NewUserDataBlock = ({ isChecked, setIsChecked, selectedType, setSelectedType }: { isChecked: boolean, setIsChecked: Dispatch<SetStateAction<boolean>>, selectedType: UserTypeEnum | undefined, setSelectedType:Dispatch<SetStateAction<UserTypeEnum | undefined>>  }) => {
  const { errors } = useAppSelector((state) => state.registrationReducer);
  const [checkboxColor, setCheckboxColor] = useState('transparent');
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [newUserData, setNewUserData] = useState<User>({
    firstName: "",
    lastName: "",
    entityName: "",
    email: "",
    phoneNumber: "",
    password: "",
    userGroup: { userGroupId: UserGroups.BAREKAM, name: "" },
    type: selectedType || UserTypeEnum.PHYSICAL_PERSON,
    locationId: {
      countryId: ARMENIA_COUNTRY_ID,
      name: "",
      abbreviation: "",
      warehouse: [],
      hasActiveWarehouse: true,
      isAllowedExport: false,
    },
  });
  const [repeatedPassword, setRepeatedPassword] = useState("");
  const [tid, setTid] = useState<number | null>(null);

  function onChange(
    value: any,
    field:
      | "firstName"
      | "lastName"
      | "entityName"
      | "email"
      | "phoneNumber"
      | "password"
      | "type"
      | "tid",
  ): void {
    if (field === "tid") {
      const numericOnly = value.toString().replace(/\D/g, "");
      // const noLeadingZeros = numericOnly.replace(/^0*/, '');

      setTid(numericOnly ? numericOnly.slice(0, 8) : "");
    } else {
      setNewUserData((prevState) => {
        return { ...prevState, [field]: value };
      });
    }
  }

  function passedValidations() {
    if (repeatedPassword !== newUserData.password) {
      // TODO: use encryption
      dispatch(
        changeErrors({ errors: { password: "passwords_do_not_match" } }),
      );

      return false;
    } else {
      return true;
    }
  }

  function signUp(): void {
    if (!isChecked) {
      setCheckboxColor('red');
      return;
    }
    if (passedValidations()) {
      dispatch(
        registerAction({
          newUserData,
          recipientTypeId: selectedType || UserTypeEnum.PHYSICAL_PERSON,
          languageId,
          tid,
        }),
      ).then((data) => {
        if (data.meta.requestStatus === "fulfilled") {
          navigate("../account-activation");
        }
      });
    }
  }

  async function googleAuthSuccess(credentialResponse: CredentialResponse) {
    try {
      dispatch(
        registerWithGoogleAccountAction({
          credentials: credentialResponse?.credential,
          languageId,
        }),
      ).then((registrationData) => {
        const response = registrationData.payload as {
          userId: number;
          error: any;
        };
  
        if (response.userId) {
          try {
            dispatch(
              loginWithGoogleAccountAction(credentialResponse?.credential),
            ).then(() => {
              const accessToken = localStorage.getItem("access_token");
  
              if (accessToken) {
                navigate("../orders/my", {
                  state: {
                    toastMessage: getMessage(
                      "register_succeeded",
                      MessageSections.ORDERS,
                      messageCache,
                      languageId,
                    ),
                    toastType: "success",
                  },
                });
              }
            });
          } catch (error) {
            console.error("Failed to login / sign up with the Google account!");
          }
        } else {
          if (
            response?.error?.response?.data.find(
              (item: any) =>
                (item["message"] as string) === "email_is_not_unique",
            )
          ) {
            try {
              dispatch(
                loginWithGoogleAccountAction(credentialResponse?.credential),
              ).then(() => {
                const accessToken = localStorage.getItem("access_token");
  
                if (accessToken) {
                  navigate("../orders/my", {
                    state: {
                      toastMessage: getMessage(
                        "register_succeeded",
                        MessageSections.ORDERS,
                        messageCache,
                        languageId,
                      ),
                      toastType: "success",
                    },
                  });
                }
              });
            } catch (error) {
              console.error(
                "Failed to login / sign up with the Google account!",
              );
            }
          } else {
            console.error(response.error);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }
  }
  
  function googleAuthError() {
    console.error("Failed to login / sign up with the Google account!");
  }

  const handleCheckbox = useCallback(() => {
    setIsChecked(prev => !prev);
  }, [setIsChecked]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <BarekamButton
          type={SimpleButtons.LINK}
          onClick={() => setSelectedType(undefined)}
        >
          <BarekamIcon variant={IconVariants.back} />
        </BarekamButton>

        <BarekamText variant={"xxl"} align={"center"} bold>
          <Message
            messageKey={"create_account"}
            sectionId={MessageSections.LOGIN}
          />
        </BarekamText>

        <Box width={"32px"} />
      </div>

      <BarekamInput
        key={"register-first-name"}
        customInputVariant={"standard"}
        placeholder={getMessage(
          "first_name",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        value={newUserData?.firstName}
        onChange={(value) => onChange(value.target.value, "firstName")}
        type={"text"}
        error={!!errors["firstName"]}
        errorMessage={
          errors["firstName"] &&
          getMessage(
            errors["firstName"],
            MessageSections.LOGIN,
            messageCache,
            languageId,
          )
        }
        width={"100%"}
        required
        validatePattern
      />

      <BarekamInput
        key={"register-last-name"}
        customInputVariant={"standard"}
        placeholder={getMessage(
          "last_name",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        value={newUserData?.lastName}
        onChange={(value) => onChange(value.target.value, "lastName")}
        type={"text"}
        error={!!errors["lastName"]}
        errorMessage={
          errors["lastName"] &&
          getMessage(
            errors["lastName"],
            MessageSections.LOGIN,
            messageCache,
            languageId,
          )
        }
        width={"100%"}
        required
        validatePattern
      />

      {selectedType === UserTypeEnum.LEGAL_ENTITY && (
        <>
          <BarekamInput
            key={"register-entity-name"}
            customInputVariant={"standard"}
            placeholder={getMessage(
              "entity_name",
              MessageSections.LOGIN,
              messageCache,
              languageId,
            )}
            value={newUserData?.entityName}
            onKeyUp={(event) => event.key === "Enter" && signUp()}
            onChange={(value) => onChange(value.target.value, "entityName")}
            type={"text"}
            error={!!errors["entityName"]}
            errorMessage={
              errors["entityName"] &&
              getMessage(
                errors["entityName"],
                MessageSections.LOGIN,
                messageCache,
                languageId,
              )
            }
            width={"100%"}
            required
            validatePattern
          />

          <BarekamInput
            key={"register-tid"}
            customInputVariant={"standard"}
            placeholder={getMessage(
              "tid",
              MessageSections.LOGIN,
              messageCache,
              languageId,
            )}
            value={tid}
            onKeyUp={(event) => event.key === "Enter" && signUp()}
            onChange={(value) => onChange(value.target.value, "tid")}
            type={"text"}
            error={!!errors["tid"]}
            errorMessage={
              errors["tid"] &&
              getMessage(
                errors["tid"],
                MessageSections.LOGIN,
                messageCache,
                languageId,
              )
            }
            width={"100%"}
            required
          />
        </>
      )}

      <BarekamInput
        key={"register-email"}
        customInputVariant={"standard"}
        placeholder={getMessage(
          "email",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        value={newUserData?.email}
        onKeyUp={(event) => event.key === "Enter" && signUp()}
        onChange={(value) => onChange(value.target.value, "email")}
        type={"email"}
        error={!!errors["email"]}
        errorMessage={
          errors["email"] &&
          getMessage(
            `hy_${errors["email"]}`,
            MessageSections.LOGIN,
            messageCache,
            languageId,
          )
        }
        width={"100%"}
        required
        validatePattern
      />

      <BarekamPhoneInput
        key={"register-phone-number"}
        value={""}
        placeholder={getMessage(
          "phone_number",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        onKeyUp={(event) => event.key === "Enter" && signUp()}
        onChange={(value: number) => onChange(value, "phoneNumber")}
        error={!!errors["phoneNumber"]}
        errorMessage={
          errors["phoneNumber"] &&
          getMessage(
            errors["phoneNumber"],
            MessageSections.LOGIN,
            messageCache,
            languageId,
          )
        }
      />

      <BarekamInput
        key={"register-password"}
        customInputVariant={"standard"}
        placeholder={getMessage(
          "password",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        value={newUserData?.password}
        onKeyUp={(event) => event.key === "Enter" && signUp()}
        onChange={(value) => onChange(value.target.value, "password")}
        type={"password"}
        error={!!errors["password"]}
        width={"100%"}
        required
      />

      <BarekamInput
        key={"register-repeat-password"}
        customInputVariant={"standard"}
        placeholder={getMessage(
          "repeat_password",
          MessageSections.LOGIN,
          messageCache,
          languageId,
        )}
        value={repeatedPassword}
        onChange={(value) => setRepeatedPassword(value.target.value)}
        onKeyUp={(event) => event.key === "Enter" && signUp()}
        type={"password"}
        error={!!errors["password"]}
        errorMessage={
          errors["password"] &&
          getMessage(
            errors["password"],
            MessageSections.LOGIN,
            messageCache,
            languageId,
          )
        }
        width={"100%"}
        height="auto"
        required
      />


      <Grid xs={12} sx={{ width: '100%', display: 'flex', alignItems: 'center', border: `1px solid ${checkboxColor}` }}>
        <BarekamCheckbox
          checked={isChecked}
          onChange={handleCheckbox}
        />
        <BarekamText variant={'m'} opaque>
          <Message messageKey={'agreement_checkbox'} sectionId={MessageSections.MAIN} isHtml />
        </BarekamText>
      </Grid>

      <BarekamButton type={AuthButtons.MAIN} size={"l"} onClick={signUp}>
        <BarekamText variant={"l"} fontWeight={700} align={"center"}>
          <Message
            messageKey={"create_account"}
            sectionId={MessageSections.LOGIN}
          />
        </BarekamText>
      </BarekamButton>

      {errors["default"] ? (
        <Alert severity="error">
          <Message
            messageKey={errors["default"]}
            sectionId={MessageSections.LOGIN}
          />
        </Alert>
      ) : (
        <></>
      )}

      <Divider style={{ width: "100%" }}>
        <BarekamText variant={"m"} opaque>
          <Message messageKey={"or"} sectionId={MessageSections.LOGIN} />
        </BarekamText>
      </Divider>

      <GoogleLogin
        onSuccess={googleAuthSuccess}
        onError={googleAuthError}
        width={500}
      />
    </>
  );
};

const SignUp = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [selectedType, setSelectedType] = useState<UserTypeEnum | undefined>();
  const navigate = useNavigate();

  return (
    <>
      <LoginContainer>
        <InputContainer>
          {selectedType ? <NewUserDataBlock isChecked={isChecked} setIsChecked={setIsChecked} selectedType={selectedType} setSelectedType={setSelectedType} /> : <TypeSelectionBlock setSelectedType={setSelectedType} />}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BarekamText variant={"m"} style={{ marginRight: "0.3rem" }}>
              <Message
                messageKey={"has_account"}
                sectionId={MessageSections.LOGIN}
              />
            </BarekamText>

            <BarekamButton
              type={SimpleButtons.LINK}
              onClick={() => navigate("../login")}
            >
              <BarekamText variant={"m"}>
                <Message
                  messageKey={"sign_in"}
                  sectionId={MessageSections.LOGIN}
                />
              </BarekamText>
            </BarekamButton>
          </div>
        </InputContainer>
      </LoginContainer>

      <Footer />
    </>
  );
};

export default SignUp;
